<template>
  <div class="page">
    <!-- Nav -->
    <nav class="nav">
      <div class="nav-inner">
        <div class="logo">
          <div class="logo-mark">
            <div class="ring ring-1"></div>
            <div class="ring ring-2"></div>
            <div class="ring ring-3"></div>
          </div>
          <span class="logo-text">palimo</span>
        </div>
        <div class="nav-links">
          <a href="#contact" class="nav-link" @click.prevent="scrollTo('contact')">Contact</a>
          <a href="#early-access" class="nav-link btn-nav" @click.prevent="scrollTo('hero')">Early Access</a>
        </div>
      </div>
    </nav>

    <!-- Hero -->
    <section class="hero">
      <div class="hero-bg">
        <div class="grain"></div>
        <div class="glow glow-1"></div>
        <div class="glow glow-2"></div>
        <div class="glow glow-3"></div>
      </div>
      <div id="hero" class="container hero-content">
        <div class="badge">Private Beta</div>
        <h1>Memory that <em>evolves.</em></h1>
        <p class="subtitle">
          Your AI agent forgets everything between sessions.
          Palimo gives it a memory that learns, corrects itself, and grows — in three lines of code.
        </p>
        <form class="hero-form" @submit.prevent="submitForm">
          <input
            v-model="email"
            type="email"
            placeholder="you@company.com"
            class="form-input"
            required
            :disabled="submitted"
          />
          <button type="submit" class="btn-primary" :disabled="submitted">
            {{ submitted ? 'You\'re in' : 'Get Early Access' }}
          </button>
        </form>
        <p v-if="submitted" class="confirm">We'll reach out to {{ submittedEmail }} soon.</p>
        <p v-else class="hint">No credit card. We onboard you personally.</p>
      </div>
    </section>

    <!-- Code -->
    <section class="section code-section">
      <div class="container">
        <div class="section-header">
          <p class="overline">Integration</p>
          <h2>Three lines. Any LLM.</h2>
          <p class="desc">Works with OpenAI, Anthropic, local models, LangChain, custom agents — anything with a system prompt.</p>
        </div>
        <div class="code-window">
          <div class="code-chrome">
            <div class="tab active">quickstart.py</div>
          </div>
          <pre class="code"><code><span class="ln"> 1</span>  <span class="kw">from</span> <span class="pkg">palimo</span> <span class="kw">import</span> <span class="typ">Palimo</span>
<span class="ln"> 2</span>
<span class="ln"> 3</span>  mem = <span class="typ">Palimo</span>(api_key=<span class="str">"pk_live_..."</span>)
<span class="ln"> 4</span>
<span class="ln"> 5</span>  <span class="cmt"># Before your LLM call — inject memory into the prompt</span>
<span class="ln"> 6</span>  ctx = mem.<span class="fn">enrich</span>(user_id=<span class="str">"usr_9k2"</span>, message=user_msg)
<span class="ln"> 7</span>  response = llm(system_prompt + ctx, user_msg)
<span class="ln"> 8</span>
<span class="ln"> 9</span>  <span class="cmt"># After — commit the turn. The archivist handles the rest.</span>
<span class="ln">10</span>  mem.<span class="fn">commit</span>(user_id=<span class="str">"usr_9k2"</span>, message=user_msg, response=response)</code></pre>
        </div>
      </div>
    </section>

    <!-- Two steps -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <p class="overline">How it works</p>
          <h2>Two calls. Full memory pipeline.</h2>
        </div>

        <div class="two-col">
          <div class="card">
            <div class="card-top">
              <span class="card-label">GET</span>
              <code>/v1/enrich</code>
            </div>
            <h3>Retrieve</h3>
            <p>Embedding search + graph traversal returns a packed context string — everything relevant about this user, compressed to 2048 tokens.</p>
            <ul class="card-stats">
              <li><strong>~50ms</strong> latency</li>
              <li><strong>0</strong> LLM calls</li>
              <li><strong>2048</strong> token budget</li>
            </ul>
          </div>
          <div class="card">
            <div class="card-top">
              <span class="card-label post">POST</span>
              <code>/v1/commit</code>
            </div>
            <h3>Remember</h3>
            <p>A fine-tuned archivist LLM extracts facts, resolves contradictions, merges duplicates, and expires stale knowledge. Returns 202 immediately.</p>
            <ul class="card-stats">
              <li><strong>9</strong> memory ops</li>
              <li><strong>Async</strong> processing</li>
              <li><strong>Local</strong> LLM</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Differentiators -->
    <section class="section section-dark">
      <div class="container">
        <div class="section-header">
          <p class="overline">Why Palimo</p>
          <h2>Not another RAG wrapper.</h2>
          <p class="desc">A full memory management pipeline with entity graphs, structured mutations, and a fine-tuned archivist model that outperforms frontier-prompted approaches.</p>
        </div>

        <div class="diff-grid">
          <div class="diff">
            <h4>$0 memory inference</h4>
            <p>The archivist runs locally on your hardware. Mem0 calls GPT-5 on every turn — at 10K users that's 200K frontier calls per day. With Palimo, it's zero.</p>
          </div>
          <div class="diff">
            <h4>Self-hosted by design</h4>
            <p>SQLite per tenant, on your servers. No data routes through us or any third party. GDPR delete is <code>rm -rf</code>.</p>
          </div>
          <div class="diff">
            <h4>9 memory operations</h4>
            <p>INSERT, SUPERSEDE, MERGE, CLARIFY, EXPIRE, REAFFIRM, DELETE, UPDATE, IGNORE. Real-world memory isn't just add and search.</p>
          </div>
          <div class="diff">
            <h4>Not a framework</h4>
            <p>Two endpoints. Palimo doesn't touch your LLM, your prompts, your tokens, or your architecture. We return a string; you use it however you want.</p>
          </div>
          <div class="diff">
            <h4>Fine-tuned, not prompted</h4>
            <p>A purpose-built model, not GPT-5 with a system prompt. Outperforms frontier-prompted approaches at a fraction of the cost. This compounds.</p>
          </div>
          <div class="diff">
            <h4>Memory that contradicts</h4>
            <p>Users change jobs, move cities, change their minds. SUPERSEDE chains track how knowledge evolves. The old shapes the new — like a palimpsest.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <p class="overline">Landscape</p>
          <h2>How we're different.</h2>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th class="hl">Palimo</th>
                <th>Mem0</th>
                <th>Letta</th>
                <th>DIY</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cost per memory call</td>
                <td class="hl">$0</td>
                <td>GPT-5 / Gemini pricing</td>
                <td>Varies</td>
                <td>Your compute</td>
              </tr>
              <tr>
                <td>Data leaves your infra</td>
                <td class="hl">Never</td>
                <td>Yes</td>
                <td>Configurable</td>
                <td>No</td>
              </tr>
              <tr>
                <td>Integration</td>
                <td class="hl">2 endpoints</td>
                <td>Managed API</td>
                <td>Full framework</td>
                <td>Build it</td>
              </tr>
              <tr>
                <td>Memory operations</td>
                <td class="hl">9 structured</td>
                <td>Basic CRUD</td>
                <td>Framework-tied</td>
                <td>Whatever you build</td>
              </tr>
              <tr>
                <td>Time to integrate</td>
                <td class="hl">Minutes</td>
                <td>Minutes</td>
                <td>Days</td>
                <td>Months</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <p class="overline">FAQ</p>
          <h2>Common questions.</h2>
        </div>
        <div class="faq-list">
          <details class="faq-item">
            <summary class="faq-q">How does Palimo compare to Mem0?</summary>
            <p class="faq-a">Mem0 calls frontier models like GPT-5 and Gemini 3.1 for memory extraction on every turn — your data routes through OpenAI or Google and you pay frontier API pricing at scale. Palimo uses a fine-tuned local archivist LLM that runs on your infrastructure. Zero per-call inference cost, and your data never leaves your servers.</p>
          </details>
          <details class="faq-item">
            <summary class="faq-q">Does Palimo require a GPU?</summary>
            <p class="faq-a">No. The archivist model is quantized to run efficiently on CPU. A GPU speeds up inference but isn't required. Runs well on Apple Silicon and standard Linux servers.</p>
          </details>
          <details class="faq-item">
            <summary class="faq-q">What are the 9 memory operations?</summary>
            <p class="faq-a">INSERT, SUPERSEDE, MERGE, CLARIFY, EXPIRE, REAFFIRM, DELETE, UPDATE, and IGNORE. Real-world memory isn't just add and search — users change jobs, move cities, contradict themselves. Palimo handles all of it with structured operations, not brute-force overwriting.</p>
          </details>
          <details class="faq-item">
            <summary class="faq-q">How long does integration take?</summary>
            <p class="faq-a">Three lines of code. Call enrich() before your LLM call to inject memory context, then commit() after to let the archivist process the turn. Works with OpenAI, Anthropic, local models, LangChain — anything with a system prompt.</p>
          </details>
          <details class="faq-item">
            <summary class="faq-q">Is Palimo open source?</summary>
            <p class="faq-a">Palimo is self-hosted — it runs on your infrastructure and your data stays with you. The Python SDK client is available via pip. The core archivist model and memory pipeline are proprietary, fine-tuned to outperform frontier-prompted memory extraction.</p>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="section cta-final">
      <div class="cta-bg">
        <div class="grain"></div>
        <div class="glow glow-4"></div>
        <div class="glow glow-5"></div>
      </div>
      <div class="container cta-final-content">
        <h2>Give your agent a memory.</h2>
        <p class="desc">Join the private beta. We set up every early user personally.</p>
        <form class="hero-form" @submit.prevent="submitForm">
          <input
            v-model="email"
            type="email"
            placeholder="you@company.com"
            class="form-input"
            required
            :disabled="submitted"
          />
          <button type="submit" class="btn-primary" :disabled="submitted">
            {{ submitted ? 'You\'re in' : 'Get Early Access' }}
          </button>
        </form>
      </div>
    </section>

    <!-- Contact -->
    <section id="contact" class="section section-dark">
      <div class="container">
        <div class="contact-layout">
          <div class="contact-info">
            <p class="overline">Get in touch</p>
            <h2>Let's talk.</h2>
            <p class="desc">Questions about Palimo, partnership opportunities, or just want to chat about AI memory architecture — reach out.</p>
          </div>
          <form class="contact-form" @submit.prevent="submitContact">
            <div class="form-row">
              <div class="form-group">
                <label for="contact-name">Name</label>
                <input id="contact-name" v-model="contactName" type="text" class="form-input" placeholder="Your name" required :disabled="contactSent" />
              </div>
              <div class="form-group">
                <label for="contact-email">Email</label>
                <input id="contact-email" v-model="contactEmail" type="email" class="form-input" placeholder="you@company.com" required :disabled="contactSent" />
              </div>
            </div>
            <div class="form-group">
              <label for="contact-message">Message</label>
              <textarea id="contact-message" v-model="contactMessage" class="form-input form-textarea" placeholder="What's on your mind?" rows="4" required :disabled="contactSent"></textarea>
            </div>
            <button type="submit" class="btn-primary btn-contact" :disabled="contactSent || contactSending">
              {{ contactSent ? 'Message sent' : contactSending ? 'Sending...' : 'Send Message' }}
            </button>
            <p v-if="contactSent" class="confirm">Thanks — we'll get back to you soon.</p>
          </form>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container footer-inner">
        <span class="footer-brand">palimo</span>
        <span class="footer-copy">Memory that evolves.</span>
        <a href="#contact" class="footer-link" @click.prevent="scrollTo('contact')">Contact us</a>
      </div>
    </footer>
  </div>
</template>

<script>
// SETUP: Replace this with your Google Apps Script web app URL
const GOOGLE_SHEET_URL = 'https://script.google.com/macros/s/AKfycbzae_jHTucR0i7UtAcJOwe_dHCs21uNR7MB6qGkyJusXAXLx4Ngf7OwcE-tgqEPKuq5Vg/exec'

export default {
  name: 'LandingPage',
  data() {
    return {
      email: '',
      submitted: false,
      submittedEmail: '',
      contactName: '',
      contactEmail: '',
      contactMessage: '',
      contactSent: false,
      contactSending: false
    }
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    async submitForm() {
      if (!this.email || this.submitted) return
      this.submittedEmail = this.email
      this.submitted = true
      try {
        await fetch(GOOGLE_SHEET_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'early_access',
            email: this.email,
            timestamp: new Date().toISOString()
          })
        })
      } catch (e) {
        console.error('Submit error:', e)
      }
    },
    async submitContact() {
      if (!this.contactName || !this.contactEmail || !this.contactMessage || this.contactSent) return
      this.contactSending = true
      try {
        await fetch(GOOGLE_SHEET_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'contact',
            name: this.contactName,
            email: this.contactEmail,
            message: this.contactMessage,
            timestamp: new Date().toISOString()
          })
        })
        this.contactSent = true
      } catch (e) {
        console.error('Contact error:', e)
      }
      this.contactSending = false
    }
  }
}
</script>

<style scoped>
/* ===================== */
/*  TOKENS               */
/* ===================== */
.page {
  --bg: #0c0c0e;
  --bg-raised: #121215;
  --bg-card: #16161a;
  --border: rgba(255,255,255,0.07);
  --text: #e2e2e6;
  --text-muted: #8b8b96;
  --text-dim: #5e5e6a;
  --accent: #e8b96e;
  --accent-2: #c97b3a;
  --teal: #4fd1a5;
  --font: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --mono: 'JetBrains Mono', 'Fira Code', monospace;
}

/* ===================== */
/*  BASE                 */
/* ===================== */
.page {
  background: var(--bg);
  min-height: 100vh;
  overflow-x: hidden;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 28px;
}

/* ===================== */
/*  NAV                  */
/* ===================== */
.nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  background: rgba(12,12,14,0.85);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border);
}

.nav-inner {
  max-width: 1000px;
  margin: 0 auto;
  padding: 18px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-mark {
  position: relative;
  width: 26px; height: 26px;
}

.ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid var(--accent);
}

.ring-1 { inset: 0; opacity: 0.25; }
.ring-2 { inset: 5px; opacity: 0.5; }
.ring-3 { inset: 9px; opacity: 1; background: var(--accent); border: none; }

.logo-text {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #fff;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-link {
  font-size: 0.85rem;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}

.nav-link:hover { color: var(--text); }

.btn-nav {
  padding: 7px 16px;
  border-radius: 7px;
  background: rgba(232,185,110,0.1);
  border: 1px solid rgba(232,185,110,0.2);
  color: var(--accent);
  font-weight: 600;
  font-size: 0.82rem;
  transition: background 0.2s, border-color 0.2s;
}

.btn-nav:hover {
  background: rgba(232,185,110,0.15);
  border-color: rgba(232,185,110,0.35);
  color: var(--accent);
}

/* ===================== */
/*  HERO                 */
/* ===================== */
.hero {
  position: relative;
  padding: 140px 0 64px;
  text-align: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.grain {
  position: absolute;
  inset: 0;
  opacity: 0.035;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 256px;
}

.glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
}

.glow-1 {
  width: 500px; height: 500px;
  top: -100px; left: 15%;
  background: radial-gradient(circle, rgba(232,185,110,0.12) 0%, transparent 70%);
}

.glow-2 {
  width: 400px; height: 400px;
  top: 50px; right: 10%;
  background: radial-gradient(circle, rgba(79,209,165,0.08) 0%, transparent 70%);
}

.glow-3 {
  width: 600px; height: 300px;
  bottom: -50px; left: 30%;
  background: radial-gradient(circle, rgba(201,123,58,0.06) 0%, transparent 70%);
}

.hero-content {
  position: relative;
  z-index: 1;
}

.badge {
  display: inline-block;
  padding: 5px 14px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  background: rgba(232,185,110,0.08);
  border: 1px solid rgba(232,185,110,0.15);
  margin-bottom: 28px;
}

h1 {
  font-size: clamp(2.8rem, 7vw, 4.5rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.05;
  color: #fff;
  margin: 0 0 24px;
}

h1 em {
  font-style: italic;
  background: linear-gradient(135deg, var(--accent) 0%, var(--teal) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  font-size: 1.1rem;
  line-height: 1.75;
  color: var(--text-muted);
  max-width: 500px;
  margin: 0 auto 40px;
}

/* ===================== */
/*  FORMS                */
/* ===================== */
.hero-form {
  display: flex;
  gap: 10px;
  max-width: 460px;
  margin: 0 auto;
}

.form-input {
  flex: 1;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-raised);
  color: var(--text);
  font-size: 0.9rem;
  font-family: var(--font);
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  border-color: rgba(232,185,110,0.4);
  box-shadow: 0 0 0 3px rgba(232,185,110,0.08);
}

.form-input::placeholder { color: var(--text-dim); }
.form-input:disabled { opacity: 0.5; }

.btn-primary {
  padding: 14px 28px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
  color: #0c0c0e;
  font-size: 0.9rem;
  font-weight: 700;
  font-family: var(--font);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(232,185,110,0.2);
}

.btn-primary:disabled {
  background: var(--teal);
  cursor: default;
}

.confirm {
  margin-top: 14px;
  font-size: 0.85rem;
  color: var(--teal);
}

.hint {
  margin-top: 14px;
  font-size: 0.8rem;
  color: var(--text-dim);
}

/* ===================== */
/*  SECTIONS             */
/* ===================== */
.section {
  padding: 80px 0;
}

.section-dark {
  background: var(--bg-raised);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.section-header {
  margin-bottom: 56px;
}

.overline {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 0 12px;
}

h2 {
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #fff;
  margin: 0 0 12px;
}

.desc {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-muted);
  max-width: 560px;
}

/* ===================== */
/*  CODE WINDOW          */
/* ===================== */
.code-window {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: #0e0e11;
  box-shadow: 0 24px 80px rgba(0,0,0,0.4);
}

.code-chrome {
  display: flex;
  padding: 0;
  background: rgba(255,255,255,0.02);
  border-bottom: 1px solid var(--border);
}

.tab {
  padding: 12px 20px;
  font-size: 0.78rem;
  font-family: var(--mono);
  color: var(--text-dim);
  border-bottom: 2px solid transparent;
}

.tab.active {
  color: var(--text);
  border-bottom-color: var(--accent);
  background: rgba(255,255,255,0.02);
}

.code {
  margin: 0;
  padding: 24px;
  font-family: var(--mono);
  font-size: 0.85rem;
  line-height: 1.85;
  overflow-x: auto;
  color: var(--text);
}

.code .ln {
  color: #3a3a44;
  user-select: none;
  display: inline-block;
  width: 2.2em;
  text-align: right;
  margin-right: 1.5em;
}

.code .kw { color: #d19a66; }
.code .pkg { color: var(--text); }
.code .typ { color: var(--teal); }
.code .str { color: #98c379; }
.code .fn { color: #61afef; }
.code .cmt { color: #4a4a56; font-style: italic; }

/* ===================== */
/*  CARDS (two-col)      */
/* ===================== */
.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.card {
  padding: 32px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  transition: border-color 0.25s;
}

.card:hover {
  border-color: rgba(232,185,110,0.2);
}

.card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.card-label {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  font-family: var(--mono);
  letter-spacing: 0.05em;
  color: var(--teal);
  background: rgba(79,209,165,0.1);
}

.card-label.post {
  color: var(--accent);
  background: rgba(232,185,110,0.1);
}

.card-top code {
  font-family: var(--mono);
  font-size: 0.82rem;
  color: var(--text-muted);
}

.card h3 {
  margin: 0 0 10px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
}

.card p {
  margin: 0 0 20px;
  font-size: 0.9rem;
  line-height: 1.65;
  color: var(--text-muted);
}

.card-stats {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 20px;
}

.card-stats li {
  font-size: 0.78rem;
  color: var(--text-dim);
}

.card-stats strong {
  color: var(--text);
  font-weight: 600;
}

/* ===================== */
/*  DIFF GRID            */
/* ===================== */
.diff-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}

.diff {
  padding: 28px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.015);
}

.diff h4 {
  margin: 0 0 10px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
}

.diff p {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.65;
  color: var(--text-muted);
}

.diff code {
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.8em;
  font-family: var(--mono);
  background: rgba(79,209,165,0.08);
  color: var(--teal);
}

/* ===================== */
/*  TABLE                */
/* ===================== */
.table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--border);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

thead th {
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: var(--text-dim);
  background: var(--bg-raised);
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
  letter-spacing: 0.02em;
}

thead th.hl { color: var(--accent); }

tbody td {
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.03);
  color: var(--text-muted);
}

tbody td:first-child {
  color: var(--text);
  font-weight: 500;
}

tbody td.hl {
  color: var(--teal);
  font-weight: 600;
}

tbody tr:last-child td { border-bottom: none; }

/* ===================== */
/*  FINAL CTA            */
/* ===================== */
.cta-final {
  position: relative;
  text-align: center;
  overflow: hidden;
}

.cta-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.glow-4 {
  position: absolute;
  width: 500px; height: 400px;
  top: 10%; left: 20%;
  background: radial-gradient(circle, rgba(232,185,110,0.08) 0%, transparent 70%);
  filter: blur(80px);
  border-radius: 50%;
}

.glow-5 {
  position: absolute;
  width: 400px; height: 400px;
  bottom: 0; right: 15%;
  background: radial-gradient(circle, rgba(79,209,165,0.06) 0%, transparent 70%);
  filter: blur(80px);
  border-radius: 50%;
}

.cta-final-content {
  position: relative;
  z-index: 1;
}

/* ===================== */
/*  FOOTER               */
/* ===================== */
.footer {
  padding: 28px 0;
  border-top: 1px solid var(--border);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-brand {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text-dim);
  letter-spacing: -0.02em;
}

.footer-copy {
  font-size: 0.8rem;
  color: var(--text-dim);
}

.footer-link {
  font-size: 0.8rem;
  color: var(--text-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}

.footer-link:hover { color: var(--text); }

/* ===================== */
/*  CONTACT FORM         */
/* ===================== */
.contact-layout {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 64px;
  align-items: start;
}

.contact-info .desc {
  margin-top: 8px;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-muted);
  letter-spacing: 0.01em;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.btn-contact {
  align-self: flex-start;
  margin-top: 4px;
}

/* ===================== */
/*  FAQ                  */
/* ===================== */
.faq-list {
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.faq-item {
  border-bottom: 1px solid var(--border);
  padding: 0;
}

.faq-item:first-child {
  border-top: 1px solid var(--border);
}

.faq-q {
  padding: 20px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: color 0.2s;
}

.faq-q:hover {
  color: var(--accent);
}

.faq-q::after {
  content: '+';
  font-size: 1.3rem;
  font-weight: 400;
  color: var(--text-dim);
  transition: transform 0.2s;
  flex-shrink: 0;
  margin-left: 16px;
}

.faq-item[open] .faq-q::after {
  content: '−';
}

.faq-q::-webkit-details-marker {
  display: none;
}

.faq-a {
  padding: 0 0 20px;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--text-muted);
  max-width: 640px;
}

/* ===================== */
/*  RESPONSIVE           */
/* ===================== */
@media (max-width: 768px) {
  .hero { padding: 140px 0 80px; }

  .two-col { grid-template-columns: 1fr; }

  .diff-grid { grid-template-columns: 1fr; }

  .hero-form { flex-direction: column; }

  .footer-inner {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .contact-layout { grid-template-columns: 1fr; gap: 40px; }
  .form-row { grid-template-columns: 1fr; }

  table { font-size: 0.78rem; }
  thead th, tbody td { padding: 10px 14px; }
}
</style>
